import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _css4, _css5, _css6;
import { css } from '@emotion/react';
import { borderRadius } from '@atlaskit/theme/constants';
import { emojiPickerBorderColor, emojiPickerBoxShadow } from '../../util/shared-styles';
import { emojiSprite, placeholder, emojiNodeStyles } from '../common/styles';
import { defaultEmojiPickerSize, emojiPickerHeight, emojiPickerHeightWithPreview, emojiPickerMinHeight, emojiPickerWidth } from '../../util/constants';
import { B200, B300, N100A, N200, N30, N30A, N50, N900 } from '@atlaskit/theme/colors';
import { emojiPickerHeightOffset } from './utils';

// Level 1 - picker
export var emojiPicker = function emojiPicker(hasPreview) {
  var size = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultEmojiPickerSize;
  var heightOffset = emojiPickerHeightOffset(size);
  return css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    background: "var(--ds-surface-overlay, white)",
    border: "".concat(emojiPickerBorderColor, " 1px solid"),
    borderRadius: "".concat(borderRadius(), "px"),
    boxShadow: emojiPickerBoxShadow,
    height: "".concat(hasPreview ? emojiPickerHeightWithPreview + heightOffset : emojiPickerHeight + heightOffset, "px"),
    width: "".concat(emojiPickerWidth, "px"),
    marginBottom: '8px',
    minWidth: "".concat(emojiPickerWidth, "px"),
    minHeight: "".concat(emojiPickerMinHeight + heightOffset, "px"),
    maxHeight: 'calc(80vh - 86px)' // ensure showing full picker in small device: mobile header is 40px (Jira) - 56px(Confluence and Atlas), reaction picker height is 24px with margin 6px,
  });
};

// Level 2

/// Category Selector

export var addButton = 'emoji-picker-add-button';
export var categorySelector = css(_defineProperty({
  flex: '0 0 auto',
  backgroundColor: "var(--ds-surface-sunken, ".concat(N30, ")"),
  ul: {
    listStyle: 'none',
    margin: '0 4px',
    padding: '3px 0',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around'
  },
  li: {
    display: 'inline-block',
    margin: 0,
    padding: 0,
    button: {
      verticalAlign: 'middle'
    }
  }
}, ".".concat(addButton), {
  color: "var(--ds-text-subtlest, ".concat(N200, ")"),
  margin: '0 0 0 5px',
  verticalAlign: 'middle'
}));
export var active = css(_defineProperty({
  color: "var(--ds-text-selected, ".concat(B300, ")")
}, '&:hover', {
  color: "var(--ds-text-selected, ".concat(B300, ")")
}));
export var disable = css(_defineProperty({
  color: "var(--ds-text-subtlest, ".concat(N50, ")"),
  cursor: 'default'
}, '&:hover', {
  color: "var(--ds-text-subtlest, ".concat(N50, ")")
}));
export var categoryStyles = css((_css4 = {
  backgroundColor: 'transparent',
  border: 0,
  color: "var(--ds-text-subtlest, ".concat(N100A, ")"),
  cursor: 'pointer',
  margin: '2px 0',
  padding: 0,
  transition: 'color 0.2s ease'
}, _defineProperty(_css4, '&::-moz-focus-inner', {
  border: '0 none',
  padding: 0
}), _defineProperty(_css4, '&:hover', {
  color: "var(--ds-text-selected, ".concat(B200, ")")
}), _css4));

/// EmojiPickerList

export var emojiPickerList = css({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  // To force Firefox/IE/Edge to shrink the list, if necessary (e.g. when upload panel in place)
  height: '0'
});

// focus style is enabled by default - turn it off
export var virtualList = css({
  overflowX: 'hidden',
  overflowY: 'auto',
  '&:focus': {
    outline: 'none'
  }
});

//// Search

export var searchIcon = css({
  opacity: 0.5
});
export var input = css((_css5 = {
  boxSizing: 'border-box',
  color: 'inherit',
  cursor: 'inherit',
  fontSize: '14px',
  outline: 'none',
  padding: '1px 0 2px 6px',
  width: '100%'
}, _defineProperty(_css5, '&:invalid', {
  boxShadow: 'none'
}), _defineProperty(_css5, '&::-ms-clear', {
  display: 'none'
}), _css5));
export var pickerSearch = css({
  boxSizing: 'border-box',
  padding: '10px 10px 11px 10px',
  width: '100%'
});

//// Loading/Spinner

export var emojiPickerSpinner = css({
  display: 'flex',
  width: '100%',
  height: '150px',
  justifyContent: 'center',
  alignItems: 'center',
  '>div': {
    flex: '0 0 auto'
  }
});

//// Category/Result

export var emojiPickerRow = css({
  marginLeft: '8px'
});
export var emojiCategoryTitle = css({
  boxSizing: 'border-box',
  color: "var(--ds-text, ".concat(N900, ")"),
  fontSize: '14px',
  padding: '5px 8px',
  textTransform: 'lowercase',
  '&:first-letter': {
    textTransform: 'uppercase'
  }
});
export var emojiItem = css((_css6 = {
  display: 'inline-block',
  textAlign: 'center',
  width: '40px'
}, _defineProperty(_css6, "& .".concat(emojiNodeStyles), {
  cursor: 'pointer',
  padding: '8px',
  borderRadius: '5px',
  width: '24px',
  height: '24px'
}), _defineProperty(_css6, "& .".concat(placeholder), {
  padding: '0',
  margin: '7px',
  minWidth: '24px',
  maxWidth: '24px'
}), _defineProperty(_css6, "& .".concat(emojiNodeStyles, " .").concat(placeholder), {
  margin: '0'
}), _defineProperty(_css6, "& .".concat(emojiNodeStyles, " > img"), {
  position: 'relative',
  left: '50%',
  top: '50%',
  transform: 'translateX(-50%) translateY(-50%)',
  maxHeight: '24px',
  maxWidth: '24px',
  display: 'block'
}), _defineProperty(_css6, "& .".concat(emojiNodeStyles, " > .").concat(emojiSprite), {
  height: '24px',
  width: '24px'
}), _css6));

/// Footer
export var emojiPickerFooter = css({
  flex: '0 0 auto'
});
export var emojiPickerFooterWithTopShadow = css({
  borderTop: "2px solid ".concat("var(--ds-border, ".concat(N30A, ")")),
  boxShadow: "0px -1px 1px 0px ".concat("var(--ds-border, rgba(0, 0, 0, 0.1))")
});
export var emojiActionsContainerWithBottomShadow = css({
  borderBottom: "2px solid ".concat("var(--ds-border, ".concat(N30A, ")")),
  boxShadow: "0px 1px 1px 0px ".concat("var(--ds-border, rgba(0, 0, 0, 0.1))")
});